'use client';

/* eslint-disable @next/next/no-img-element */
import { SubmissionResult } from '@conform-to/react';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as FormPrimitive from '@radix-ui/react-form';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import * as ScrollAreaMenu from '@radix-ui/react-scroll-area';
import * as SelectPrimitive from '@radix-ui/react-select';
import { clsx } from 'clsx';
import { Check, ChevronDown, ChevronUp, Heart, ShoppingCart, User, X } from 'lucide-react';
import { useFormatter, useTranslations } from 'next-intl';
import React, {
  forwardRef,
  Ref,
  startTransition,
  useActionState,
  useEffect,
  useState,
  useTransition,
} from 'react';

import { Stream, Streamable } from '@/vibes/mom/lib/streamable';
import { Button } from '@/vibes/mom/primitives/button';
import { Logo } from '@/vibes/mom/primitives/logo';
import { Price } from '@/vibes/mom/primitives/price-label';
import SearchWrapper from '~/components/algolia/autocomplete-search';
import { AccountDetails, TopLevelMenuItem } from '~/components/header';
import { Link } from '~/components/link';
import { env } from '~/lib/atom/env';
import { countryCodeSchema } from '~/lib/atom/types/country';
import { currencyCodeSchema } from '~/lib/atom/types/currency';
import { PriceConfig } from '~/lib/atom/types/price-config';
import { countryCurrencyMap } from '~/lib/currency-map';

import LoadingDots from '../loading-dots';

import { useNavigationOverlay } from './hooks/use-navigation-overlay';
import { MobileNav } from './mobile-nav';
import { NavigationSchema } from './utils';

interface Link {
  label: string;
  href: string;
  groups?: Array<{
    label?: string;
    href?: string;
    links: Array<{
      label: string;
      href: string;
    }>;
  }>;
}

interface Locale {
  id: string;
  label: string;
}

interface Currency {
  code: string;
  name: string;
}

interface Country {
  code: string;
  name?: string;
  flag?: string;
}

type Action<State, Payload> = (
  state: Awaited<State>,
  payload: Awaited<Payload>,
) => State | Promise<State>;

export type SearchResult =
  | {
      type: 'products';
      title: string;
      products: Array<{
        id: string;
        title: string;
        href: string;
        price?: Price;
        image?: { src: string; alt: string };
      }>;
    }
  | {
      type: 'links';
      title: string;
      links: Array<{ label: string; href: string }>;
    };

type LocaleAction = Action<SubmissionResult | null, FormData>;
type CurrencyAction = Action<SubmissionResult | null, FormData>;
type CountryAction = Action<SubmissionResult | null, FormData>;

interface Props {
  className?: string;
  isFloating?: boolean;
  accountHref: string;
  cartDetails?: Streamable<{
    totalQuantity: number;
    amount: { value: number; currencyCode: string };
  } | null>;
  cartHref: string;
  linksPosition?: 'center' | 'left' | 'right';
  locales?: Locale[];
  activeLocaleId?: string;
  localeAction?: LocaleAction;
  currencies?: Currency[];
  countryData: Country[];
  countryAction?: CountryAction;
  activeCountryCode?: string;
  showCountryDialog?: boolean;
  geolocatedCountryCode: string;
  activeCurrencyId?: string;
  activeCurrencySymbol?: string;
  currencyAction?: CurrencyAction;
  logo?: Streamable<string | { src: string; alt: string } | null>;
  logoWidth?: number;
  logoHeight?: number;
  logoHref?: string;
  logoLabel?: string;
  mobileLogo?: Streamable<string | { src: string; alt: string } | null>;
  mobileLogoWidth?: number;
  mobileLogoHeight?: number;
  cartLabel?: string;
  accountLabel?: string;
  openSearchPopupLabel?: string;
  searchLabel?: string;
  mobileMenuTriggerLabel?: string;
  topLevelMenuItems: Streamable<TopLevelMenuItem[]> | Promise<TopLevelMenuItem[]>;
  isLoggedIn?: boolean;
  isMomPrimeUser?: boolean;
  accountDetails?: Streamable<AccountDetails | null>;
  priceConfig: PriceConfig;
  anonymousUserToken: string;
}

const MobileMenuButton = forwardRef<
  React.ComponentRef<'button'>,
  { open: boolean } & React.ComponentPropsWithoutRef<'button'>
>(({ open, className, ...rest }, ref) => {
  return (
    <button
      {...rest}
      className={clsx(
        'group relative rounded-lg p-2 outline-0 ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors focus-visible:ring-2',
        className,
      )}
      ref={ref}
    >
      <div className="flex h-4 w-4 origin-center transform flex-col justify-between overflow-hidden transition-all duration-300">
        <div
          className={clsx(
            'h-px origin-left transform bg-[var(--nav-mobile-button-icon,hsl(var(--foreground)))] transition-all duration-300',
            open ? 'translate-x-10' : 'w-7',
          )}
        />
        <div
          className={clsx(
            'h-px transform rounded bg-[var(--nav-mobile-button-icon,hsl(var(--foreground)))] transition-all delay-75 duration-300',
            open ? 'translate-x-10' : 'w-7',
          )}
        />
        <div
          className={clsx(
            'h-px origin-left transform bg-[var(--nav-mobile-button-icon,hsl(var(--foreground)))] transition-all delay-150 duration-300',
            open ? 'translate-x-10' : 'w-7',
          )}
        />

        <div
          className={clsx(
            'absolute top-2 flex transform items-center justify-between bg-[var(--nav-mobile-button-icon,hsl(var(--foreground)))] transition-all duration-500',
            open ? 'w-12 translate-x-0' : 'w-0 -translate-x-10',
          )}
        >
          <div
            className={clsx(
              'absolute h-px w-4 transform bg-[var(--nav-mobile-button-icon,hsl(var(--foreground)))] transition-all delay-300 duration-500',
              open ? 'rotate-45' : 'rotate-0',
            )}
          />
          <div
            className={clsx(
              'absolute h-px w-4 transform bg-[var(--nav-mobile-button-icon,hsl(var(--foreground)))] transition-all delay-300 duration-500',
              open ? '-rotate-45' : 'rotate-0',
            )}
          />
        </div>
      </div>
    </button>
  );
});

MobileMenuButton.displayName = 'MobileMenuButton';

const navButtonClassName =
  'flex items-center gap-1 group transition hover:underline relative rounded-lg bg-[var(--nav-button-background,transparent)] text-white leading-[0px] p-1.5 text-[var(--nav-button-icon,hsl(var(--foreground)))] ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors focus-visible:outline-0 focus-visible:ring-2 @4xl:hover:bg-[var(--nav-button-background-hover,hsl(var(--contrast-100)))] @4xl:hover:text-[var(--nav-button-icon-hover,hsl(var(--foreground)))]';

/**
 * This component supports various CSS variables for theming. Here's a comprehensive list, along
 * with their default values:
 *
 * ```css
 * :root {
 *   --nav-focus: hsl(var(--primary));
 *   --nav-background: hsl(var(--background));
 *   --nav-floating-border: hsl(var(--foreground) / 10%);
 *   --nav-link-text: hsl(var(--foreground));
 *   --nav-link-text-hover: hsl(var(--foreground));
 *   --nav-link-background: transparent;
 *   --nav-link-background-hover: hsl(var(--contrast-100));
 *   --nav-link-font-family: var(--font-family-body);
 *   --nav-group-text: hsl(var(--foreground));
 *   --nav-group-text-hover: hsl(var(--foreground));
 *   --nav-group-background: transparent;
 *   --nav-group-background-hover: hsl(var(--contrast-100));
 *   --nav-group-font-family: var(--font-family-body);
 *   --nav-sub-link-text: hsl(var(--contrast-500));
 *   --nav-sub-link-text-hover: hsl(var(--foreground));
 *   --nav-sub-link-background: transparent;
 *   --nav-sub-link-background-hover: hsl(var(--contrast-100));
 *   --nav-sub-link-font-family: var(--font-family-body);
 *   --nav-button-icon: hsl(var(--foreground));
 *   --nav-button-icon-hover: hsl(var(--foreground));
 *   --nav-button-background: hsl(var(--background));
 *   --nav-button-background-hover: hsl(var(--contrast-100));
 *   --nav-menu-background: hsl(var(--background));
 *   --nav-menu-border: hsl(var(--foreground) / 5%);
 *   --nav-mobile-background: hsl(var(--background));
 *   --nav-mobile-divider: hsl(var(--contrast-100));
 *   --nav-mobile-button-icon: hsl(var(--foreground));
 *   --nav-mobile-link-text: hsl(var(--foreground));
 *   --nav-mobile-link-text-hover: hsl(var(--foreground));
 *   --nav-mobile-link-background: transparent;
 *   --nav-mobile-link-background-hover: hsl(var(--contrast-100));
 *   --nav-mobile-link-font-family: var(--font-family-body);
 *   --nav-mobile-sub-link-text: hsl(var(--contrast-500));
 *   --nav-mobile-sub-link-text-hover: hsl(var(--foreground));
 *   --nav-mobile-sub-link-background: transparent;
 *   --nav-mobile-sub-link-background-hover: hsl(var(--contrast-100));
 *   --nav-mobile-sub-link-font-family: var(--font-family-body);
 *   --nav-search-background: hsl(var(--background));
 *   --nav-search-border: hsl(var(--foreground) / 5%);
 *   --nav-search-divider: hsl(var(--foreground) / 5%);
 *   --nav-search-icon: hsl(var(--contrast-500));
 *   --nav-search-empty-title: hsl(var(--foreground));
 *   --nav-search-empty-subtitle: hsl(var(--contrast-500));
 *   --nav-search-result-title: hsl(var(--foreground));
 *   --nav-search-result-title-font-family: var(--font-family-mono);
 *   --nav-search-result-link-text: hsl(var(--foreground));
 *   --nav-search-result-link-text-hover: hsl(var(--foreground));
 *   --nav-search-result-link-background: hsl(var(--background));
 *   --nav-search-result-link-background-hover: hsl(var(--contrast-100));
 *   --nav-search-result-link-font-family: var(--font-family-body);
 *   --nav-cart-count-text: hsl(var(--background));
 *   --nav-cart-count-background: hsl(var(--foreground));
 *   --nav-locale-background: hsl(var(--background));
 *   --nav-locale-link-text: hsl(var(--contrast-400));
 *   --nav-locale-link-text-hover: hsl(var(--foreground));
 *   --nav-locale-link-text-selected: hsl(var(--foreground));
 *   --nav-locale-link-background: transparent;
 *   --nav-locale-link-background-hover: hsl(var(--contrast-100));
 *   --nav-locale-link-font-family: var(--font-family-body);
 * }
 * ```
 */
export const Navigation = forwardRef(function Navigation(
  {
    className,
    cartHref,
    cartDetails: streamableCartDetails,
    accountHref,
    logo: streamableLogo,
    logoHref = '/',
    logoLabel = 'Home',
    activeLocaleId,
    localeAction,
    locales,
    countryData,
    activeCountryCode,
    showCountryDialog,
    geolocatedCountryCode,
    currencies,
    activeCurrencyId,
    activeCurrencySymbol,
    currencyAction,
    countryAction,
    topLevelMenuItems,
    cartLabel = 'Cart',
    isLoggedIn,
    isMomPrimeUser,
    accountLabel = 'Account',
    accountDetails: streamableAccountDetails,
    priceConfig,
    anonymousUserToken,
  }: Props,
  ref: Ref<HTMLDivElement>,
) {
  const format = useFormatter();

  const t = useTranslations('Components.Header');

  useNavigationOverlay();

  return (
    <div className={clsx('bg-mom-brandColor text-white', className)}>
      <Stream value={topLevelMenuItems}>
        {(builderNav) => <NavigationSchema menuItems={builderNav} />}
      </Stream>
      <header className="2xl:container mx-auto flex min-h-[50px] flex-row items-center justify-between gap-1 overflow-y-visible pl-0 pr-2 xl:container md:flex-row md:gap-6 md:px-4 md:py-3 lg:min-h-[76px] lg:gap-5 lg:py-0 xl:gap-10">
        <div className="flex items-center justify-between md:gap-4 lg:gap-12">
          <MobileNav
            accountDetails={streamableAccountDetails}
            links={topLevelMenuItems}
            logo={streamableLogo}
            logoHref={logoHref}
            logoLabel={logoLabel}
          />
          <Logo
            className="mr-auto flex h-10 w-28 md:w-36 lg:h-16 lg:w-48"
            href={logoHref}
            isMomPrimeUser={isMomPrimeUser}
            label={logoLabel}
            logo={streamableLogo}
          />
        </div>

        <div className="hidden min-h-[50px] grow justify-center rounded-lg bg-mom-brandColorDarker md:flex">
          <SearchWrapper
            anonymousUserToken={anonymousUserToken}
            isLoggedIn={isLoggedIn}
            priceConfig={priceConfig}
          />
        </div>

        {/* Icon Buttons */}
        <div className="flex items-center gap-1 xl:gap-4">
          <CurrencyAndLocaleSwitcher
            activeCountryCode={activeCountryCode}
            activeCurrencyId={activeCurrencyId}
            activeCurrencySymbol={activeCurrencySymbol}
            countryAction={countryAction}
            countryData={countryData}
            currencies={currencies}
            currencyAction={currencyAction}
            geolocatedCountryCode={geolocatedCountryCode}
            showCountryDialog={showCountryDialog}
          />

          {/* Locale / Language Dropdown */}
          {locales && locales.length > 1 && localeAction ? (
            <LocaleForm
              action={localeAction}
              activeLocaleId={activeLocaleId}
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              locales={locales as [Locale, Locale, ...Locale[]]}
            />
          ) : null}

          <Link
            aria-label={accountLabel}
            className={clsx('hidden lg:flex', navButtonClassName)}
            href={accountHref}
          >
            <User
              className="text-white transition-all duration-300 ease-in-out group-hover:fill-white"
              size={20}
              strokeWidth={2}
            />
            {isLoggedIn ? t('Account.account') : t('Account.login')}
          </Link>

          <Link
            aria-label={accountLabel}
            className={clsx('hidden lg:flex', navButtonClassName)}
            href="/account/wishlists"
          >
            <Heart
              className="text-white transition-all duration-300 ease-in-out group-hover:fill-white"
              size={20}
              strokeWidth={2}
            />
            {t('Account.wishlists')}
          </Link>

          <Link
            aria-label={cartLabel}
            className={clsx(
              'xl:pt- relative gap-2 rounded-none lg:bg-mom-brandColorDarker lg:px-5 lg:pb-7 lg:pt-7 xl:px-7 xl:pb-7',
              navButtonClassName,
            )}
            href={cartHref}
          >
            <div className="relative">
              <ShoppingCart
                className="text-mom-gold transition-all duration-300 ease-in-out group-hover:fill-mom-gold"
                size={24}
                strokeWidth={2}
              />
              <Stream
                fallback={
                  <span className="absolute -right-2 -top-2 flex h-5 w-5 animate-pulse items-center justify-center rounded-full bg-contrast-200" />
                }
                value={streamableCartDetails}
              >
                {(cartDetails) =>
                  cartDetails?.totalQuantity != null &&
                  cartDetails.totalQuantity > 0 && (
                    <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full border-2 border-mom-brandColorDarker bg-white text-center font-heading-condensed text-xs font-semibold leading-[0px] text-mom-black">
                      {cartDetails.totalQuantity}
                    </span>
                  )
                }
              </Stream>
            </div>

            <Stream
              fallback={<span className="h-4 w-12 animate-pulse rounded-md bg-contrast-200" />}
              value={streamableCartDetails}
            >
              {(cartDetails) => {
                return cartDetails != null ? (
                  <span className="font-heading-condensed text-[16px] font-semibold leading-none text-white">
                    {format.number(cartDetails.amount.value, {
                      style: 'currency',
                      currency: cartDetails.amount.currencyCode || 'GBP',
                      maximumFractionDigits: 2,
                    })}
                  </span>
                ) : (
                  <span className="text-white">{t('Basket')}</span>
                );
              }}
            </Stream>
          </Link>
        </div>
      </header>

      <NavigationMenu.Root
        className="relative z-10 mx-auto hidden items-center gap-1 overflow-y-visible lg:block lg:gap-8"
        ref={ref}
      >
        <NavigationMenu.List className="center 2xl:container m-0 mx-auto grid list-none grid-flow-col px-4 xl:container">
          <Stream
            fallback={
              <ul className="center 2xl:container m-0 mx-auto grid animate-pulse list-none grid-flow-col px-4 lg:container">
                <li className="flex items-center justify-center">
                  <span className="block h-4 w-10 rounded-md bg-contrast-100" />
                </li>
                <li className="flex items-center justify-center">
                  <span className="block h-4 w-14 rounded-md bg-contrast-100" />
                </li>
                <li className="flex items-center justify-center">
                  <span className="block h-4 w-24 rounded-md bg-contrast-100" />
                </li>
                <li className="flex items-center justify-center">
                  <span className="block h-4 w-16 rounded-md bg-contrast-100" />
                </li>
                <li className="flex items-center justify-center">
                  <span className="block h-4 w-10 rounded-md bg-contrast-100" />
                </li>
                <li className="flex items-center justify-center">
                  <span className="block h-4 w-14 rounded-md bg-contrast-100" />
                </li>
                <li className="flex items-center justify-center">
                  <span className="block h-4 w-24 rounded-md bg-contrast-100" />
                </li>
                <li className="flex items-center justify-center">
                  <span className="block h-4 w-16 rounded-md bg-contrast-100" />
                </li>
                <li className="flex items-center justify-center">
                  <span className="block h-4 w-10 rounded-md bg-contrast-100" />
                </li>
                <li className="flex items-center justify-center">
                  <span className="block h-4 w-14 rounded-md bg-contrast-100" />
                </li>
                <li className="flex items-center justify-center">
                  <span className="block h-4 w-24 rounded-md bg-contrast-100" />
                </li>
              </ul>
            }
            value={topLevelMenuItems}
          >
            {(builderNav) =>
              builderNav.map((item: TopLevelMenuItem) => {
                return (
                  <NavigationMenu.Item key={item.url}>
                    <NavigationMenu.Trigger
                      asChild
                      className="group flex h-full select-none items-center justify-center gap-[2px] px-3 py-2 font-heading-condensed text-[16px] font-semibold uppercase leading-none tracking-wider outline-none hover:bg-white hover:text-black data-[state=open]:bg-white data-[state=open]:text-black lg:px-2 lg:py-4 xl:px-2"
                      style={
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        {
                          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                          ['--background-color' as string]: item.backgroundColour,
                          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                          ['--text-color' as string]: item.textColour,
                          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                          ['--hover-background-color' as string]: item.hoverBackgroundColour,
                          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                          ['--hover-text-color' as string]: item.hoverTextColour,
                        } as React.CSSProperties
                      }
                    >
                      <Link href={item.url} prefetch="hover">
                        {item.name}
                      </Link>
                    </NavigationMenu.Trigger>
                    <NavigationMenu.Content className="data-[motion=from-start]:animate-enterFromLeft data-[motion=from-end]:animate-enterFromRight data-[motion=to-start]:animate-exitToLeft data-[motion=to-end]:animate-exitToRight absolute left-0 top-0 w-full">
                      <div className="2xl:container m-0 mx-auto flex w-full justify-evenly gap-2 px-4 pb-14 pt-10 lg:container">
                        {/* Dropdown sections */}
                        {item.columns?.map((column, colIndex) => (
                          <div
                            className={`flex w-full flex-col gap-1 ${column.imageItem?.image ? 'max-w-[300px]' : ''} ${colIndex !== 0 ? '' : ''} ${item.columns && colIndex !== item.columns.length - 1 ? 'border-blue-500' : 'border-green-500'}`}
                            key={colIndex}
                          >
                            {/* eslint-disable-next-line no-nested-ternary */}
                            {column.children && column.children.length > 0 ? (
                              <>
                                <h3 className="mb-3 font-heading-condensed text-[16px] font-semibold uppercase leading-[22px] text-mom-gold">
                                  {column.name}
                                </h3>
                                <ul
                                  className="m-0 flex list-none flex-col gap-1 p-0"
                                  key={colIndex}
                                >
                                  {column.children.map((child, childIndex) => (
                                    <React.Fragment key={childIndex}>
                                      <li>
                                        <NavigationMenu.Link
                                          asChild
                                          className={
                                            child.type === 'all'
                                              ? 'inline-flex text-[16px] font-semibold capitalize leading-[24px] text-mom-black no-underline outline-none hover:underline'
                                              : 'inline-flex select-none text-[15px] leading-6 text-mom-black no-underline outline-none hover:underline'
                                          }
                                        >
                                          <Link href={child.url} prefetch="hover">
                                            {child.name}
                                          </Link>
                                        </NavigationMenu.Link>
                                      </li>
                                      {child.children?.map((nestedChild, nestedChildIndex) => (
                                        <li key={nestedChildIndex}>
                                          <NavigationMenu.Link
                                            asChild
                                            className="outline-hidden inline-flex select-none text-[16px] leading-6 text-mom-black no-underline hover:underline"
                                          >
                                            <Link href={nestedChild.url} prefetch="hover">
                                              {nestedChild.name}
                                            </Link>
                                          </NavigationMenu.Link>
                                        </li>
                                      ))}
                                    </React.Fragment>
                                  ))}
                                </ul>
                              </>
                            ) : column.imageItem ? (
                              <NavigationMenu.Link asChild className="group hover:underline">
                                <Link
                                  data-promo={column.imageItem.promo}
                                  data-promo-creative={column.imageItem.promoCreative}
                                  href={column.imageItem.destinationUrl}
                                  itemProp="url"
                                  passHref
                                  prefetch="hover"
                                  title={column.imageItem.headingText}
                                >
                                  <div className="relative flex flex-col gap-1">
                                    <img
                                      alt={column.imageItem.imageAltText}
                                      className="rounded-sm object-cover"
                                      height={200}
                                      loading="lazy"
                                      src={column.imageItem.image}
                                      width={300}
                                    />
                                    <div className="flex flex-col gap-1 p-2">
                                      <h3 className="text-pretty font-heading-condensed text-[16px] font-extrabold uppercase leading-[22px] tracking-wide text-mom-black group-hover:underline">
                                        {column.imageItem.headingText}
                                      </h3>
                                      <p className="text-pretty text-sm text-mom-black group-hover:underline">
                                        {column.imageItem.tagline}
                                      </p>
                                    </div>
                                  </div>
                                </Link>
                              </NavigationMenu.Link>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </NavigationMenu.Content>
                  </NavigationMenu.Item>
                );
              })
            }
          </Stream>
        </NavigationMenu.List>
        <div className="perspective-[2000px] shadow-l absolute left-0 top-full flex w-full justify-center">
          <NavigationMenu.Viewport className="data-[state=open]:animate-scaleIn data-[state=closed]:animate-scaleOut relative h-[var(--radix-navigation-menu-viewport-height)] w-full origin-[top_center] overflow-hidden bg-white transition-[width,_height] duration-300" />
        </div>
      </NavigationMenu.Root>

      <div className="flex min-h-[50px] w-full items-center justify-center px-1 pb-1 md:hidden">
        <div className="min-h-[50px] w-full rounded bg-mom-brandColorDarker">
          <SearchWrapper
            anonymousUserToken={anonymousUserToken}
            isLoggedIn={isLoggedIn}
            priceConfig={priceConfig}
          />
        </div>
      </div>
    </div>
  );
});

Navigation.displayName = 'Navigation';

function CurrencyAndLocaleSwitcher({
  currencies,
  activeCurrencyId,
  currencyAction,
  countryAction,
  countryData,
  activeCountryCode,
  showCountryDialog,
  activeCurrencySymbol,
  geolocatedCountryCode,
}: {
  currencies?: Currency[];
  activeCurrencyId?: string;
  currencyAction?: CurrencyAction;
  countryAction?: CountryAction;
  activeCountryCode?: string;
  countryData: Country[];
  showCountryDialog?: boolean;
  activeCurrencySymbol?: string;
  geolocatedCountryCode: string;
}) {
  const [open, setOpen] = useState<boolean>(showCountryDialog ?? false);
  const [isPending, runTransition] = useTransition();

  const activeCountry = activeCountryCode || geolocatedCountryCode;
  const activeCurrency =
    activeCurrencyId || countryCurrencyMap[activeCountry] || env.NEXT_PUBLIC_DEFAULT_CURRENCY_CODE;

  const t = useTranslations('Components.Header.CountryAndCurrencyPicker');

  const avoidDefaultDomBehavior = (e: Event) => {
    e.preventDefault();
  };

  const saveCountryCurrencyPreference = (formData: FormData) => {
    runTransition(async () => {
      const country = countryCodeSchema.safeParse(formData.get('country'));
      const currency = currencyCodeSchema.safeParse(formData.get('currency'));

      const promises = [];

      if (country.success && countryAction) {
        const countryFormData = new FormData();

        countryFormData.append('value', country.data);

        promises.push(countryAction(null, countryFormData));
      }

      if (currency.success && currencyAction) {
        const currencyFormData = new FormData();

        currencyFormData.append('id', currency.data);

        promises.push(currencyAction(null, currencyFormData));
      }

      await Promise.all(promises);

      setOpen(false);
    });
  };

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [open]);

  return (
    <Dialog.Root modal={false} onOpenChange={setOpen} open={open}>
      <Dialog.Trigger asChild>
        <Button
          className="font-base flex h-10 w-20 items-center gap-2 bg-mom-brandColorDarker text-base transition-opacity hover:underline hover:opacity-80 [&_span]:p-1.5"
          shape="rounded"
          size="x-small"
          variant="ghost"
        >
          <img
            alt={countryData.find((c) => c.code === activeCountryCode)?.name || 'Country flag'}
            className="aspect-square h-6 w-6 rounded-full object-cover"
            src={`https://flagcdn.com/${activeCountryCode?.toLowerCase() || 'gb'}.svg`}
          />
          <div className="text-sm font-normal text-white">{activeCurrencySymbol}</div>
          <ChevronDown className="text-white" />
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <div
          aria-hidden="true"
          className="data-[state=open]:animate-overlayShow fixed inset-0 z-10 bg-black bg-opacity-60"
          data-state={open ? 'open' : 'closed'}
        />
        <Dialog.Content
          className={clsx(
            'fixed left-0 right-0 top-0 z-50 h-full max-h-[400px] min-h-[350px] w-full overflow-auto bg-white shadow-lg duration-300 ease-in-out focus:outline-none md:max-h-[350px]',
            open ? 'animate-slideDown opacity-100' : 'opacity-0',
          )}
          onInteractOutside={avoidDefaultDomBehavior}
          onPointerDownOutside={avoidDefaultDomBehavior}
        >
          <div className="relative mx-auto flex h-full w-full max-w-[800px] flex-col justify-center gap-1 p-6 md:p-8">
            <FormPrimitive.Form action={saveCountryCurrencyPreference}>
              <FormPrimitive.Submit asChild>
                <Button
                  aria-label={t('close')}
                  className="!absolute right-2 top-2 md:right-4 md:top-4"
                  disabled={isPending}
                  shape="circle"
                  size="x-small"
                  variant="ghost"
                >
                  <X />
                </Button>
              </FormPrimitive.Submit>
              <Dialog.Title className="text-(hsl(var(--primary))) font-heading-condensed text-[24px] font-semibold uppercase leading-[32px]">
                {t('heading')}
              </Dialog.Title>
              <Dialog.Description className="DialogDescription">
                {t('subHeading')}
              </Dialog.Description>

              <div className="flex flex-col items-center gap-4">
                <div className="mt-3 grid w-full gap-4 md:grid-cols-2 md:gap-6">
                  {countryAction ? (
                    <CountryForm
                      activeCountryCode={activeCountry}
                      countryData={countryData}
                      isPending={isPending}
                    />
                  ) : null}

                  {currencies && currencies.length > 1 ? (
                    <CurrencyForm
                      activeCurrencyId={activeCurrency}
                      currencies={currencies}
                      isPending={isPending}
                    />
                  ) : null}
                </div>
                <FormPrimitive.Submit asChild>
                  <Button
                    className="w-full [&>_span]:gap-1"
                    disabled={isPending}
                    shape="rounded"
                    size="medium"
                    variant="primary"
                  >
                    {isPending ? (
                      <>
                        Updating <LoadingDots colour="text-white" />
                      </>
                    ) : (
                      <>{t('close')}</>
                    )}
                  </Button>
                </FormPrimitive.Submit>
              </div>
            </FormPrimitive.Form>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function LocaleForm({
  action,
  locales,
  activeLocaleId,
}: {
  activeLocaleId?: string;
  action: LocaleAction;
  locales: [Locale, ...Locale[]];
}) {
  const [lastResult, formAction] = useActionState(action, null);
  const activeLocale = locales.find((locale) => locale.id === activeLocaleId);

  useEffect(() => {
    // eslint-disable-next-line no-console
    if (lastResult?.error) console.log(lastResult.error);
  }, [lastResult?.error]);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={clsx('hidden items-center gap-1 lg:flex', navButtonClassName)}
      >
        {activeLocale?.id ?? locales[0].id}
        <ChevronDown size={16} strokeWidth={1.5} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          className="z-50 max-h-80 overflow-y-scroll rounded-xl bg-[var(--nav-locale-background,hsl(var(--background)))] p-2 shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 @4xl:w-32 @4xl:rounded-2xl @4xl:p-2"
          sideOffset={16}
        >
          {locales.map(({ id, label }) => (
            <DropdownMenu.Item
              className={clsx(
                'cursor-default rounded-lg bg-[var(--nav-locale-link-background,transparent)] px-2.5 py-2 font-[family-name:var(--nav-locale-link-font-family,var(--font-family-body))] text-sm font-medium text-[var(--nav-locale-link-text,hsl(var(--contrast-400)))] outline-none ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors hover:bg-[var(--nav-locale-link-background-hover,hsl(var(--contrast-100)))] hover:text-[var(--nav-locale-link-text-hover,hsl(var(--foreground)))]',
                {
                  'text-[var(--nav-locale-link-text-selected,hsl(var(--foreground)))]':
                    id === activeLocaleId,
                },
              )}
              key={id}
              onSelect={() => {
                // eslint-disable-next-line @typescript-eslint/require-await
                startTransition(async () => {
                  const formData = new FormData();

                  formData.append('id', id);
                  formAction(formData);
                });
              }}
            >
              {label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function CurrencyForm({
  currencies,
  activeCurrencyId,
  isPending,
}: {
  activeCurrencyId?: string;
  currencies: Currency[];
  isPending: boolean;
}) {
  const t = useTranslations('Components.Header.CountryAndCurrencyPicker');

  return (
    <div className="w-full">
      <div className="mb-1 text-base font-medium leading-6 text-black">{t('currencyLabel')}</div>
      <FormPrimitive.FormField name="currency">
        <SelectPrimitive.Root defaultValue={activeCurrencyId} name="currency">
          <SelectPrimitive.Trigger
            className={clsx(
              'flex w-full items-center justify-between gap-1 rounded border border-contrast-100 bg-white px-2 py-1 transition-colors hover:border-contrast-300 hover:bg-contrast-100',
              isPending && 'cursor-wait opacity-70',
            )}
            disabled={isPending}
          >
            <SelectPrimitive.Value placeholder={activeCurrencyId} />
            <SelectPrimitive.Icon>
              <ChevronDown size={16} strokeWidth={1.5} />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>

          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              align="start"
              className="z-50 max-h-[var(--radix-select-content-available-height)] w-[var(--radix-select-trigger-width)] overflow-y-scroll rounded bg-background p-2 shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
              position="popper"
            >
              <ScrollAreaMenu.Root className="h-full w-full" type="auto">
                <SelectPrimitive.ScrollUpButton className="flex items-center justify-center py-2">
                  <ChevronUp size={16} strokeWidth={1.5} />
                </SelectPrimitive.ScrollUpButton>

                <SelectPrimitive.Viewport>
                  <ScrollAreaMenu.Viewport className="h-full w-full">
                    {currencies.map((currency) => (
                      <SelectPrimitive.Item
                        className={clsx(
                          'flex w-full cursor-pointer items-center justify-between rounded px-2.5 py-2 text-sm font-medium text-contrast-400 outline-none ring-primary transition-colors hover:bg-contrast-100 hover:text-foreground focus:bg-contrast-100 focus:text-foreground',
                          {
                            'text-foreground': currency.code === activeCurrencyId,
                          },
                        )}
                        key={currency.code}
                        value={currency.code}
                      >
                        <SelectPrimitive.ItemText>
                          {currency.code} - {currency.name}
                        </SelectPrimitive.ItemText>
                        <SelectPrimitive.ItemIndicator className="inline-flex items-center">
                          <Check size={16} strokeWidth={1.5} />
                        </SelectPrimitive.ItemIndicator>
                      </SelectPrimitive.Item>
                    ))}
                  </ScrollAreaMenu.Viewport>
                  <ScrollAreaMenu.Scrollbar className="w-[4px] px-2 py-1" orientation="vertical">
                    <ScrollAreaMenu.Thumb className="bg-[rgba(0, 0, 0, 0.3)] rounded" />
                  </ScrollAreaMenu.Scrollbar>
                </SelectPrimitive.Viewport>
              </ScrollAreaMenu.Root>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>
      </FormPrimitive.FormField>
    </div>
  );
}

function CountryForm({
  countryData,
  activeCountryCode,
  isPending,
}: {
  countryData: Country[];
  activeCountryCode?: string;
  isPending: boolean;
}) {
  const t = useTranslations('Components.Header.CountryAndCurrencyPicker');

  return (
    <div className="w-full">
      <div className="mb-1 text-base font-medium leading-6 text-black">{t('countryLabel')}</div>
      <FormPrimitive.FormField name="country">
        <SelectPrimitive.Root defaultValue={activeCountryCode} name="country">
          <SelectPrimitive.Trigger
            className={clsx(
              'flex w-full items-center justify-between gap-1 rounded border border-contrast-100 bg-white px-2 py-1 transition-colors hover:border-contrast-300 hover:bg-contrast-100',
              isPending && 'cursor-wait opacity-70',
            )}
            disabled={isPending}
          >
            <SelectPrimitive.Value placeholder={activeCountryCode} />
            <SelectPrimitive.Icon>
              <ChevronDown size={16} strokeWidth={1.5} />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>

          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              align="start"
              className="z-50 max-h-[400px] w-[var(--radix-select-trigger-width)] overflow-y-scroll rounded bg-background p-2 shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
              position="popper"
            >
              <ScrollAreaMenu.Root className="h-full w-full" type="auto">
                <SelectPrimitive.ScrollUpButton className="flex items-center justify-center py-2">
                  <ChevronUp size={16} strokeWidth={1.5} />
                </SelectPrimitive.ScrollUpButton>

                <SelectPrimitive.Viewport>
                  <ScrollAreaMenu.Viewport className="h-full w-full">
                    {countryData.map((country) => (
                      <SelectPrimitive.Item
                        className={clsx(
                          'flex w-full cursor-pointer items-center justify-between rounded px-2.5 py-2 text-sm font-medium text-contrast-400 outline-none ring-primary transition-colors hover:bg-contrast-100 hover:text-foreground focus:bg-contrast-100 focus:text-foreground',
                          {
                            'text-foreground': country.code === activeCountryCode,
                          },
                        )}
                        key={country.code}
                        value={country.code}
                      >
                        <SelectPrimitive.ItemText>
                          <div className="flex items-center gap-2">
                            <img
                              alt={country.name}
                              className="aspect-square h-5 w-5 rounded-full object-cover"
                              src={country.flag}
                            />
                            <div>{country.name}</div>
                          </div>
                        </SelectPrimitive.ItemText>
                        <SelectPrimitive.ItemIndicator className="inline-flex items-center">
                          <Check size={16} strokeWidth={1.5} />
                        </SelectPrimitive.ItemIndicator>
                      </SelectPrimitive.Item>
                    ))}
                  </ScrollAreaMenu.Viewport>
                  <ScrollAreaMenu.Scrollbar className="w-[4px] px-2 py-1" orientation="vertical">
                    <ScrollAreaMenu.Thumb className="bg-[rgba(0, 0, 0, 0.3)] rounded" />
                  </ScrollAreaMenu.Scrollbar>
                </SelectPrimitive.Viewport>
              </ScrollAreaMenu.Root>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>
      </FormPrimitive.FormField>
    </div>
  );
}
